// SimpleDescriberModal — R6-revamp (pt89Y, Feb 20, 2026, Randy).
//
// Dead-simple popup. Two fields, save/cancel. Nothing else. Opens on
// "+ New Describer" (create mode) OR on a click of an existing describer
// chip (edit mode).
//
// Data:
//   { slug (auto), header (20 char), description (freetext) }
// The description feeds the Chore Browse-Suggestions LLM prompt so
// user-typed guidance shapes what chores/tasks get proposed.

import { useEffect, useState } from "react";
import { X, FloppyDisk, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function SimpleDescriberModal({ open, onClose, initial, existingCount, onSave, onDelete }) {
  const isEdit = !!initial;
  const [header, setHeader] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setHeader(initial?.header || "");
      setDescription(initial?.description || "");
    }
  }, [open, initial]);

  const canSave = header.trim().length >= 2 && !saving;
  const atCap = !isEdit && (existingCount || 0) >= 3;

  const save = async () => {
    if (!canSave) { toast.error("Enter a header first."); return; }
    if (atCap) { toast.error("You've already got 3 describers."); return; }
    setSaving(true);
    try {
      const payload = {
        slug: initial?.slug || header.trim().toLowerCase().replace(/[^a-z0-9]+/g, "_").slice(0, 20),
        header: header.trim().slice(0, 20),
        description: description.trim().slice(0, 400),
      };
      await onSave(payload);
      onClose && onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save the describer.");
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!initial?.slug) return;
    if (!window.confirm(`Remove "${initial.header}"?`)) return;
    setSaving(true);
    try {
      await onDelete(initial.slug);
      onClose && onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't remove the describer.");
    } finally { setSaving(false); }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[270] bg-black/50 backdrop-blur-sm flex items-center justify-center p-3"
      onClick={onClose} data-testid="describer-modal">
      <div onClick={(e) => e.stopPropagation()}
        className="modal-safe bg-[#FDFCF8] rounded-2xl w-full max-w-md shadow-2xl border hairline flex flex-col">
        <div className="px-5 py-3 border-b hairline flex items-center gap-2">
          <div className="flex-1">
            <div className="font-serif-display text-lg text-[#1C1B1A]">
              {isEdit ? "Edit describer" : "New describer"}
            </div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68] mt-0.5">
              {isEdit ? "Update the guidance the Chore AI reads." : `${existingCount || 0}/3 used — feeds the Chore AI`}
            </div>
          </div>
          <button onClick={onClose} className="p-1 text-[#6B6A68] hover:text-[#1C1B1A]"
            data-testid="describer-modal-close" aria-label="Close"><X size={17} /></button>
        </div>

        <div className="p-5 space-y-4">
          <label className="block">
            <span className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68]">
              Header <span className="normal-case text-[#8A8A88] italic">({header.length}/20)</span>
            </span>
            <input value={header} onChange={(e) => setHeader(e.target.value.slice(0, 20))}
              data-testid="describer-modal-header-input"
              placeholder="e.g. Vehicles"
              maxLength={20}
              className="mt-1 w-full px-3 py-2 border hairline rounded-md bg-white text-sm" />
          </label>

          <label className="block">
            <span className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68]">
              Description <span className="normal-case text-[#8A8A88] italic">— feeds the Chore AI</span>
            </span>
            <textarea value={description} onChange={(e) => setDescription(e.target.value.slice(0, 400))}
              data-testid="describer-modal-description-input"
              placeholder="Describe what you want the Chore AI to know when suggesting chores or tasks tied to this describer."
              rows={4}
              maxLength={400}
              className="mt-1 w-full px-3 py-2 border hairline rounded-md bg-white text-sm resize-none" />
            <span className="text-[10px] text-[#8A8A88] italic mt-0.5 inline-block">{description.length}/400</span>
          </label>
        </div>

        <div className="px-5 py-3 border-t hairline flex items-center gap-2">
          {isEdit && (
            <button onClick={remove} disabled={saving}
              data-testid="describer-modal-delete"
              className="text-xs px-2.5 py-2 rounded text-[#C16E5A] hover:bg-[#FBEBE4] flex items-center gap-1 disabled:opacity-50">
              <Trash size={12} weight="duotone" /> Remove
            </button>
          )}
          <span className="flex-1" />
          <button onClick={onClose} data-testid="describer-modal-cancel"
            className="text-xs px-3 py-2 rounded text-[#6B6A68] hover:bg-[#F0F1EE]">Cancel</button>
          <button onClick={save} disabled={!canSave || atCap}
            data-testid="describer-modal-save"
            className="text-xs px-4 py-2 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] disabled:opacity-50 flex items-center gap-1.5">
            <FloppyDisk size={12} weight="duotone" /> Save
          </button>
        </div>
      </div>
    </div>
  );
}
